import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CircularProgress,
  List,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material';

import { sortByLanguageName } from 'src/helper/helper';
import { useShoppingCart } from 'src/components/report/ShoppingCartContext';
import { useAvailableLanguages } from 'src/components/reportConfig/hooks/useAvailableLanguages';

import { styles } from './index.styles';

interface LanguageSelectionProps {
  onClose: () => void;
  handleUserChange: () => void;
}

const LanguageSelection: FC<LanguageSelectionProps> = ({
  onClose,
  handleUserChange,
}) => {
  const { shoppingCartLanguage, setShoppingCartLanguage } = useShoppingCart();
  const { t } = useTranslation('common');
  const { shoppingCartLanguages, languageNamesPerCode, loading, fetched } =
    useAvailableLanguages() || [];

  return (
    <List>
      {loading || !fetched ? (
        <Stack
          direction="row"
          sx={{
            justifyContent: 'center',
            py: 1,
          }}
        >
          <CircularProgress />
        </Stack>
      ) : shoppingCartLanguages.length ? (
        shoppingCartLanguages.sort(sortByLanguageName).map((language) => (
          <ListItemButton
            sx={styles.listItem(
              language?.abbreviation === shoppingCartLanguage?.abbreviation,
            )}
            id={`language-${language.abbreviation}`}
            key={language.abbreviation}
            title={languageNamesPerCode?.[language.code] ?? ''}
            onClick={() => {
              setShoppingCartLanguage(language);
              handleUserChange();
              setTimeout(onClose, 100);
            }}
          >
            <Stack direction="column">
              <Typography>
                {languageNamesPerCode?.[language.code] ?? ''}
              </Typography>
              <Typography sx={styles.subline}>
                {language.abbreviation.toUpperCase()}
              </Typography>
            </Stack>
          </ListItemButton>
        ))
      ) : (
        t('common:noDataAvailable')
      )}
    </List>
  );
};

export default LanguageSelection;
